import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  Button,
  Datagrid,
  downloadCSV,
  List,
  TextField,
  TopToolbar,
  useGetOne,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { Link } from "@mui/material";
import useNotify from "../unit/useNotify";

import { CloseOutlined, CopyAllOutlined } from "@mui/icons-material";
import PostPagination from "../unit/PostPagination";

import { RaRecord } from "react-admin";
import CampaignsListAside from "../campaigns/CampaignsListAside";
import CampaignsListFilters from "../campaigns/CampaignsListFilters";
import sendRequest from "../dataProvider/sendRequest";
import CustomButtonCancel from "../unit/CustomButtonCancel";
import CustomButtonSubmit from "../unit/CustomButtonSubmit";
import CustomDateField from "../unit/CustomDateField";
import CustomEditIcon from "../unit/CustomEditIcon";
import CustomEditLinkProductIcon from "../unit/CustomEditLinkProductIcon";
import CustomEditRemoveIcon from "../unit/CustomEditRemoveIcon";
import CustomEditTermIcon from "../unit/CustomEditTermIcon";
import CustomExportButton from "../unit/CustomExportButton";
import CustomLoading from "../unit/CustomLoading";
import CustomUniqueURLIcon from "../unit/CustomUniqueURLIcon";
import SwitchIcon from "../unit/svgIcon/SwitchIcon";
import SwitchOnIcon from "../unit/svgIcon/SwitchOnIcon";
import CouponCodeAdd from "./CouponCodeAdd";
import CouponCodeEdit from "./CouponCodeEdit";
import { hmLightTheme } from "../themes/hmTheme";
import HmLoading from "../unit/HmLoading";
import NoResults from "../unit/NoResults";
import NoPermission from "../unit/NoPermission";
import formatDate from "../unit/formatDate";

const CouponCodeListActions = ({ ...props }) => {
  return (
    <>
      <TopToolbar sx={{ width: "100%" }}>
        <CouponCodeAdd />
        <CustomExportButton />
      </TopToolbar>
    </>
  );
};
const CouponCodeEmpty = () => {
  return (
    <Box sx={{ width: "100%" }}>
      <CouponCodeListActions />
      <Typography className="no-results">
        <NoResults />
      </Typography>
    </Box>
  );
};
const CustomStatusField = ({ ...props }) => {
  const record = useRecordContext();
  return <span>{record?.iStatus == 1 ? "Active" : "Inactive"}</span>;
};
const CustomReductionField = ({ ...props }) => {
  const record = useRecordContext();
  const value = record?.fValue;
  const type = record?.sType;
  if (type == "discount") {
    const percentage = value + "%";
    return <span>{percentage}</span>;
  } else {
    return <span>${value}</span>;
  }
};

const couponcodeTypeExporter = (records: any) => {
  let data = [];
  let title = {};

  data = records.map((record: any) => {
    const { id } = record;
    return {
      id: id,
      sCode: `"${record.sCode}"`,
      fValue: record?.fValue,
      dtStart: formatDate(record?.dtStart, "YYYY-MM-DD"),
      dtEnd: formatDate(record?.dtEnd, "YYYY-MM-DD"),
      bankerName: record?.bankerName,
    };
  });
  title = {
    id: "Coupon ID",
    sCode: "Coupon Code",
    fValue: "Reduction in Fees",
    dtStart: "Activation",
    dtEnd: "Expiration",
    bankerName: "Creator",
  };
  return {
    title: title,
    data: data,
  };
};
const CustomExporter = (records: any) => {
  const filterData = couponcodeTypeExporter(records);

  let csvTitle = filterData.title;
  let data = filterData.data;
  const csvData = [{ ...csvTitle }, ...data];
  const csvDataStr = csvData.map((row) => Object.values(row).join(","));
  downloadCSV(csvDataStr.join("\n"), "Coupon Codes");
};

const CouponCodeList = () => {
  const refresh = useRefresh();
  const [editRecord, setEditRecord] = React.useState<RaRecord | null>(null);
  const [open, setOpen] = React.useState(false);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const [record, setRecord] = React.useState<{ total: number } | null>(null);
  const [currentTab, setCurrentTab] = React.useState("info");
  const [productList, setProductList] = React.useState([]);
  const permissions =
    localStorage.getItem("bbaccess")?.toString().split(",") || [];
  if (!permissions.includes("personal_campaigns")) {
    return <NoPermission />;
  }
  const { data, isLoading, error } = useGetOne("total/campaign", { id: 1 });
  React.useEffect(() => {
    setRecord(data);
  }, [data]);

  const reqUrl = "products/shortList";
  React.useEffect(() => {
    const fetchData = async () => {
      const responseData = await sendRequest(reqUrl, { id: 0 });
      if (responseData) {
        setProductList(responseData);
      }
    };
    fetchData();
  }, []);

  const total = record ? record.total : 0;

  const handleClose = () => {
    setOpen(false);
    setEditRecord(null);
    refresh();
  };

  const handleCancel = () => {
    setEditRecord(null);
  };

  const CustomButton = ({ onClick }) => {
    const record = useRecordContext();
    if (!record) return null;
    const refresh = useRefresh();

    const handleClick = (showTab) => {
      onClick(record);
      setCurrentTab(showTab);
    };
    const handleChangeStatus = () => {
      let newStatus = record.iStatus == 1 ? 0 : 1;

      var reqUrl = "couponcodes/actions/changeStatus";
      var response = sendRequest(reqUrl, {
        id: record.id,
        data: {
          status: newStatus,
        },
      });
      response
        .then((res) => {
          if (res.code == 200) {
            successNotify("Status changed successfully");
            refresh();
          } else {
            errorNotify("Failed to change status");
          }
        })
        .catch((error) => {
          errorNotify("Error changing status: " + error.message);
        });
    };
    const handleRemove = () => {
      var reqUrl = `couponcodes/actions/delete`;
      sendRequest(reqUrl, { id: record?.id }).then((response) => {
        if (response && response.code == "200") {
          successNotify("Data deleted successfully");
          handleClose();
        } else {
          errorNotify("Data not deleted");
        }
      });
      setOpen(false);
      setEditRecord(null);
    };

    const color = "red";
    const size = "22px";
    const fileSrc = "/public/icon/action-delete.svg";

    return (
      <>
        <CustomEditIcon onClick={() => handleClick("info")} label="Edit" />
        <CustomEditLinkProductIcon
          onClick={() => handleClick("linkProducts")}
          label="Link products"
        />
        {/* <CustomEditTermIcon
          onClick={() => handleClick("special")}
          label="Set terms"
        /> */}
        <Tooltip
          title={`` + (record?.iStatus == 1 ? "Deactivate" : "Activate") + ``}
          placement="left"
        >
          <IconButton
            size="small"
            onClick={handleChangeStatus}
            sx={{ marginLeft: "5px", p: 1 }}
          >
            {record?.iStatus == 1 ? (
              <SwitchOnIcon color="red" />
            ) : (
              <SwitchIcon color="green" />
            )}
          </IconButton>
        </Tooltip>
        {/* <CustomUniqueURLIcon
          onClick={() => handleClick("uniqueURL")}
          label="Unique URL"
        /> */}
        <CustomEditRemoveIcon onClick={() => handleRemove()} />
      </>
    );
  };
  const CustomCouponCode = ({ ...props }) => {
    const record = useRecordContext();
    const value = record?.sCode;
    const handleCopy = () => {
      navigator.clipboard.writeText(value);
      successNotify("Code copied to clipboard");
    };
    return (
      <Tooltip title={"Click to copy"} placement="left">
        <Link
          component="span"
          onClick={() => handleCopy()}
          className="numericText"
          underline="none"
          sx={{
            cursor: "pointer",
            textDecoration: "none",
            "&.RaLink-link": {
              color: "#1976d2",
            },
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {value}
        </Link>
      </Tooltip>
    );
  };
  const handleButtonClick = (record: any) => {
    setOpen(true);
    setEditRecord(record);
  };
  const CustomRowStyle = (record: any) => {
    if (!record) return {};

    if (record?.iStatus == 1) {
      return {
        backgroundColor: "#fff",
        "&.MuiTableRow-hover:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      };
    } else {
      return {
        backgroundColor: "#f5f5f5",
        "& td.MuiTableCell-root": {
          color: "gray",
        },
        "& td.MuiTableCell-root span": {
          color: "gray",
        },
        "& td.MuiTableCell-root a": {
          color: "gray",
        },
        "& td.MuiTableCell-root p": {
          color: "gray",
        },
      };
    }
  };

  const formatDecimal = (num, decimals = 2) => {
    return Number(Math.round(num + "e" + decimals) + "e-" + decimals);
  };
  return (
    <>
      <List
        actions={<CouponCodeListActions />}
        filters={CampaignsListFilters}
        aside={<CampaignsListAside totalData={total} selected="couponcodes" />}
        sort={{ field: "created_at", order: "DESC" }}
        exporter={CustomExporter}
        sx={{
          width: "100%",
          borderRadius: "0px",
          wordBreak: "break-word",
          overflowX: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "normal",
          "& .MuiCard-root": {
            borderRadius: "0px",
          },
          "& .RaList-actions form .filter-field .MuiInputBase-input": {
            width: "230px",
          },
          "& .RaList-actions .RaFilterForm-filterFormInput:first-child .MuiInputBase-root::before":
            {
              display: "none",
            },
          "& .RaList-actions .filter-field:first-child .MuiFormControl-root": {
            marginLeft: 0,
          },
        }}
        empty={false}
        emptyWhileLoading={false}
        loading={<HmLoading />}
        pagination={<PostPagination />}
        perPage={25}
      >
        <Datagrid
          optimized
          rowClick={false}
          empty={false}
          rowSx={CustomRowStyle}
          size="small"
          id="table-couponcodes"
          className="hm-list-datagrid"
          sx={{
            ".MuiTableCell-root": {
              padding: "6px",
            },
            ".RaDatagrid-headerCell:first-child": {
              borderTopLeftRadius: "0px",
            },
            ".RaDatagrid-headerCell:last-child": {
              borderTopRightRadius: "0px",
            },
            overflowY: "auto",
            "& .column-groups": {
              md: { display: "none" },
              lg: { display: "table-cell" },
            },
            "& .Mui-active": {
              color: "gray",
            },
            "& .MuiTableSortLabel-root": {
              padding: "5px",
            },
            "& .RaDatagrid-headerCell": {
              color: "gray",
              fontSize: "1rem",
              backgroundColor: "#edf4fb",
              wordWrap: "break-word",
            },
            "& .MuiTableCell-paddingCheckbox": {
              display: "none",
            },
            "& .MuiTableCell-root.column-id": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            },
            "& .MuiTableCell-root.column-fValue": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            },
            "& .MuiTableCell-root.column-sUrl": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-created_at": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
            },
            "& .MuiTableCell-root.column-dtStart": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
            },
            "& .MuiTableCell-root.column-dtEnd": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
            },
            "& .MuiTableCell-root.column-column-action": {
              minWidth: "300px",
              width: "300px",
              maxWidth: "300px",
            },
            "& .MuiTableCell-root.column-bankerEmail": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
          }}
        >
          <TextField source="id" label="Coupon #" cellClassName="numericText" />
          {/* <TextField source="sCode" label="Coupon Code" sortable={false} /> */}
          <CustomCouponCode
            source="sCode"
            label="Coupon Code"
            sortable={false}
          />
          <CustomReductionField
            source="fValue"
            label="Reduction in Fees"
            sortable={false}
          />

          {/* <CustomStatusField source="sStatus" label="Status" sortable={false} /> */}
          <CustomDateField
            source="dtStart"
            label="Activation"
            onlyShowDate={true}
            sortable={false}
            cellClassName="dateText"
          />
          <CustomDateField
            source="dtEnd"
            label="Expiration"
            onlyShowDate={true}
            sortable={false}
            cellClassName="dateText"
          />
          <TextField source="bankerName" label="Creator" sortable={false} />
          {/* <TextField source="created_by" label="Created By" sortable={false} /> */}
          <CustomButton
            source="column-action"
            label="Action"
            onClick={handleButtonClick}
            sortable={false}
          />
        </Datagrid>
        <CustomLoading />
      </List>
      <Dialog
        id="dialog-couponcode-edit"
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="md"
        PaperProps={{
          component: "form",
          onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            let form = document.querySelector(
              ".couponcode_edit_dialog_form form"
            );
            let formData = new FormData(form as HTMLFormElement);
            let formJson = Object.fromEntries(formData.entries());
            console.log("couponcode formJson===>", formJson);

            if (currentTab == "info") {
              if (formJson.sType == "") {
                errorNotify("Type is required");
                return;
              }

              if (formJson.sType === "amount") {
              } else if (formJson.sType === "discount") {
                const fValue = parseInt(formJson?.fValue as string).toString();
                formJson.fValue = fValue;
                const fValueNum = Number(formJson?.fValue);
                if (fValueNum > 0 && fValueNum <= 100) {
                } else {
                  errorNotify("Invalid discount value");
                  return;
                }
              }
            } else if (currentTab == "linkProducts") {
              let select_products: any[] = [];
              let select = document.querySelectorAll(
                ".couponcode_edit_dialog_form .partners_select_product"
              );
              select.forEach((element) => {
                if ((element as HTMLInputElement)?.checked) {
                  select_products.push((element as HTMLInputElement)?.value);
                }
              });
              formJson.productId = select_products.join(",");
            }
            formJson.saveType = currentTab;
            const reqUrl = `couponcodes/actions/edit`;
            const response = await sendRequest(reqUrl, {
              id: editRecord?.id,
              data: formJson,
            });
            if (response && response.code == "200") {
              successNotify("Data saved successfully");
              handleClose();
            } else {
              errorNotify(response?.message || "Data not saved!");
            }
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            borderBottom: "1px solid #e5e5e5",
            color: "#465384",
            textTransform: "uppercase",
          }}
        >
          {/* Vendor Edit Record #{editRecord?.id} */}
          {currentTab == "info" && "Coupon Code"}
          {currentTab == "linkProducts" && "Link Products"}
          {currentTab == "special" && "Set Terms"}
          {currentTab == "uniqueURL" && "Unique URL"}
          {currentTab == "remove" && "Remove"}
          <IconButton
            style={{ position: "absolute", right: "10px", top: "10px" }}
            onClick={handleClose}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              width: "100%",
              pt: 1,
              height: "50vh",
              maxHeight: "50vh",
            }}
          >
            {editRecord && (
              <CouponCodeEdit
                id={editRecord.id}
                basePath="/couponcodes"
                resource="couponcodes"
                productList={productList}
                curTab={currentTab}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <CustomButtonCancel onClick={handleClose} />
          {currentTab != "uniqueURL" && currentTab != "special" && (
            <CustomButtonSubmit />
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CouponCodeList;
